import { Color } from "@repo/app-types/constants/colors";
import { getPassRateColor } from "@repo/app-types/constants/passRate";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface TrainingAssessmentTemplateScoreLabelProps {
  /** The score as a number from 0-1, null if not applicable, or undefined if not available. */
  score: number | null | undefined;
  className?: string;
}

/**
 * Renders the same percentage / fallback text as
 * `TrainingAssessmentTemplateScorePill`, but as a bare label without the
 * pill background, ring, or padding — for inline use inside other surfaces.
 */
export const TrainingAssessmentTemplateScoreLabel: FC<
  TrainingAssessmentTemplateScoreLabelProps
> = (props) => {
  const { score, className } = props;

  if (score === undefined) {
    return (
      <span className={twMerge("text-gray-700 tabular-nums", className)}>
        --
      </span>
    );
  }

  if (score === null) {
    return (
      <span className={twMerge("text-gray-700 tabular-nums", className)}>
        n/a
      </span>
    );
  }

  return (
    <span
      className={twMerge(
        "tabular-nums",
        passRateTextColorClass(score),
        className,
      )}
    >
      {(score * 100).toFixed(0)}%
    </span>
  );
};

function passRateTextColorClass(score: number): string {
  const color = getPassRateColor(score);
  switch (color) {
    case Color.Emerald:
      return "text-emerald-700";
    case Color.Green:
      return "text-green-700";
    case Color.Lime:
      return "text-lime-700";
    case Color.Amber:
      return "text-amber-700";
    case Color.Orange:
      return "text-orange-700";
    case Color.Red:
      return "text-red-700";
    case Color.Gray:
      return "text-gray-700";
    default:
      assertUnreachable(color);
  }
}
